import SalesOrderFilter from './SalesOrderFilter';
import { SalesOrder, SalesOrderFilters } from '../../../../app/models/SalesOrder';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';

interface Props {
    salesOrders: SalesOrder[];
    totalRows: number;
    loading?: boolean;
    edit?: (id: number) => void;
    destroy?: (id: number) => void;
    filter: SalesOrderFilters;
    setFilter: (filter: SalesOrderFilters) => void;
    callbackSuccess: () => void;
    onSearch: () => void;
}

const cell: React.CSSProperties = {
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
};

const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const SalesOrderDataTable = ({
    salesOrders,
    totalRows,
    loading = false,
    edit,
    destroy,
    filter,
    setFilter,
    callbackSuccess,
    onSearch
}: Props) => {
    const columns = [
        {
            name: 'ID',
            selector: (row: any) => row.id,
            sortable: true,
            sortField: 'id',
            width: '60px',
            cell: (row: SalesOrder) => <span style={cell}>#{row.id}</span>
        },
        {
            name: 'RUT Sociedad',
            selector: (row: any) => row.society_rut,
            sortable: false,
            width: '130px',
            cell: (row: SalesOrder) => <span style={cell}>{row.society_rut || '—'}</span>
        },
        {
            name: 'Sociedad',
            selector: (row: any) => row.society,
            sortable: false,
            width: '200px',
            cell: (row: SalesOrder) => <span style={cell}>{row.society || '—'}</span>
        },
        {
            name: 'RUT Cliente',
            selector: (row: any) => row.client_rut,
            sortable: false,
            width: '130px',
            cell: (row: SalesOrder) => <span style={cell}>{row.client_rut || '—'}</span>
        },
        {
            name: 'Cliente',
            selector: (row: any) => row.client,
            sortable: false,
            width: '350px',
            cell: (row: SalesOrder) => <span style={cell}>{row.client || '—'}</span>
        },
        {
            name: 'Moneda',
            selector: (row: any) => row.currency,
            sortable: false,
            width: '80px',
            cell: (row: SalesOrder) => <span style={cell}>{row.currency || '—'}</span>
        },
        {
            name: 'Valor Neto',
            selector: (row: any) => row.total_neto,
            sortable: false,
            width: '130px',
            cell: (row: SalesOrder) => (
                <span
                    style={{
                        ...cell,
                        display: 'block',
                        textAlign: 'right',
                        width: '100%',
                    }}
                >
                    {row.total_neto != null
                        ? `$${Number(row.total_neto).toLocaleString('es-CL')}`
                        : '—'}
                </span>
            )
        },
        {
            name: 'Condición de Pago',
            selector: (row: any) => row.payment_condition,
            sortable: false,
            width: '160px',
            cell: (row: SalesOrder) => <span style={cell}>{row.payment_condition || '—'}</span>
        },
        {
            name: 'Días',
            selector: (row: any) => row.days,
            sortable: false,
            width: '65px',
            cell: (row: SalesOrder) => <span style={cell}>{row.days != null ? row.days : '—'}</span>
        },
        {
            name: 'Fecha Creación',
            selector: (row: any) => row.created_at,
            sortable: true,
            sortField: 'created_at',
            width: '155px',
            cell: (row: SalesOrder) => <span style={cell}>{formatDate(row.created_at)}</span>
        },
        {
            name: 'Acciones',
            selector: (row: any) => row?.id,
            sortable: false,
            width: '150px',
            cell: (row: SalesOrder) => (
                <div>
                    {edit && (
                        <ButtonTableAction
                            callbackFunction={() => edit(row?.id ?? -1)}
                            classIcon={'mdi mdi-pencil'}
                            colorIcon={'text-warning'}
                            errorMessage={'No se puede editar este registro.'}
                            title={'Editar'}
                        />
                    )}
                    {destroy && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row?.id ?? -1)}
                            classIcon={'mdi mdi-delete'}
                            colorIcon={'text-danger'}
                            errorMessage={'No se puede eliminar este registro.'}
                            title={'Eliminar'}
                        />
                    )}
                </div>
            )
        }
    ];

    return (
        <>
            <div className="row mt-3">
                <div className="col">
                    <SalesOrderFilter filter={filter} setFilter={setFilter} onSearch={onSearch} />
                </div>
            </div>

            <DefaultDataTable
                columns={columns}
                data={salesOrders}
                progressPending={loading}
                paginationTotalRows={totalRows}
                filter={filter}
                setFilter={setFilter}
            />
        </>
    );
};

export default SalesOrderDataTable;
